import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { CURRENCIES } from 'src/features/payStations/types';
import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { type GeneralSmartParkError } from 'src/types';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

const RESOURCE = '/reservations/:parkingId/vehicle-stay-reservation-type/';

// The backend zeroes the seconds when storing these dates, so the comparisons have to
// use the same precision - otherwise a period of seconds passes as a valid one
const toMinutes = (date: Date) => Math.floor(date.getTime() / 60_000);

const commonReservationTypeFormSchema = z.object({
    reservation_type: z.nativeEnum(RESERVATION_TYPE).default(RESERVATION_TYPE.HOURLY),
    allowed_spaces: z.number(),
    currency: z.nativeEnum(CURRENCIES).default(CURRENCIES.EUR),
    reserve_space: z.boolean().default(false),
    entry_tolerance_minutes: z.number().optional(),
    exit_tolerance_minutes: z.number().optional(),
    valid_from: z.coerce.date(),
    name: z.string().min(1).max(255),
    description: z.string().optional().default(''),
    points: z.array(z.string()).optional(),

    zones: z.array(z.string()).nullish(),
    allow_lists: z.array(z.number()).nullish(),
    max_duration: z.number().nullish(),
    max_start_date_days: z.number().nullish(),

    price: z.number().nullish(),
    tax_percent: z
        .string()
        .regex(/^-?\d{0,20}(?:\.\d{0,4})?$/)
        .nullish(),
    tax_amount: z.number().nullish(),
    price_with_tax: z.number().nullish(),
});

export const createReservationTypeFormSchema = commonReservationTypeFormSchema
    .extend({
        paid: z.boolean(),
    })
    .superRefine((data, ctx) => {
        if (!data.paid) return;

        if (!data.price) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['price'],
                message: 'Price is required when paid is enabled',
            });
        }

        if (!data.tax_percent) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tax_percent'],
                message: 'Tax percent is required when paid is enabled',
            });
        }

        if (!data.tax_amount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tax_amount'],
                message: 'Tax amount is required when paid is enabled',
            });
        }

        if (!data.price_with_tax) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['price_with_tax'],
                message: 'Price with tax is required when paid is enabled',
            });
        }
    });
export type CreateReservationTypeFormSchemaValues = z.infer<typeof createReservationTypeFormSchema>;

export const createFixedReservationTypeFormSchema = commonReservationTypeFormSchema
    .extend({
        valid_to: z.coerce.date(),
        fixed_duration_start: z.coerce.date(),
        fixed_duration_end: z.coerce.date(),
        paid: z.boolean(),
    })
    .superRefine((data, ctx) => {
        // The backend stores these with a minute precision, so anything below it ends up as an empty period
        if (toMinutes(data.valid_to) <= toMinutes(data.valid_from)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['valid_to'],
                message: validationMessagesMap.validationErrorValidToBeforeValidFrom,
            });
        }

        if (toMinutes(data.fixed_duration_end) <= toMinutes(data.fixed_duration_start)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fixed_duration_end'],
                message: validationMessagesMap.validationErrorDurationEndBeforeStart,
            });
        }

        if (!data.paid) return;

        if (data.price == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['price'],
                message: 'Price is required when paid is enabled',
            });
        }

        if (!data.tax_percent) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tax_percent'],
                message: 'Tax percent is required when paid is enabled',
            });
        }

        if (!data.tax_amount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tax_amount'],
                message: 'Tax amount is required when paid is enabled',
            });
        }

        if (!data.price_with_tax) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['price_with_tax'],
                message: 'Price with tax is required when paid is enabled',
            });
        }
    });

export type CreateFixedReservationTypeFormSchemaValues = z.infer<typeof createFixedReservationTypeFormSchema>;

interface DTO extends CreateReservationTypeFormSchemaValues {
    parkingId: string;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ parkingId, ...data }: DTO): Promise<ReservationType> => {
    return axios.post(formatResourcePath(RESOURCE, { parkingId }), data);
};

interface UseMutationOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export { useMutationHook as useCreateReservationType };
